import { createFeatureSelector, createSelector, Store } from '@ngrx/store';
import { APPLICATION_STATE_KEY, IApplicationState } from '../reducers/application.reducer';
import { EDateFilterType, IMenuItem, INavItem, MenuItemModel } from '../../../layout/navigation/models/nav-item.model';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { selectCurrentRoute, selectUrl } from './router.selector';
import { UrlSegment } from '@angular/router';
import { SCREEN_SIZE_BREAKPOINTS } from '../../configurations/application.config';
import { USER_PREFERENCES_REDUCER } from '../reducers/user-preferences.reducer';

const selectAppState = createFeatureSelector<IApplicationState>(APPLICATION_STATE_KEY);
const selectLoading = createSelector(selectAppState, ({ loading }) => loading);
const selectScreenSize = createSelector(selectAppState, (state): number => state?.screenSize);
const selectIsDesktop = createSelector(
  selectScreenSize,
  (screenSize): boolean => screenSize >= SCREEN_SIZE_BREAKPOINTS.md,
);
const selectNavigation = createSelector(selectAppState, ({ navigation }): INavItem[] | null => navigation);
const selectCurrentPageUrl = createSelector(selectCurrentRoute, (currentRoute: { url: UrlSegment[] }) =>
  currentRoute?.url.map(({ path }) => path).join('/'),
);

const selectFavouriteSubmenu = createSelector(
  selectNavigation,
  USER_PREFERENCES_REDUCER.favouritePagesUrl,
  (navigation, favouritePagesUrl) => {
    if (!navigation) return null;

    const favouritePages = favouritePagesUrl.reduce((acc, url) => {
      navigation.forEach((navItem) => {
        const menuItem = getMenuItem(navItem.subNav.items || [], url);
        if (menuItem) acc.push(menuItem);
      });
      return acc;
    }, [] as IMenuItem[]);

    return favouritePages.length
      ? new MenuItemModel({
          items: favouritePages,
          label: 'Favorites',
        })
      : null;
  },
);

const selectActiveNavMenuItem = createSelector(
  selectNavigation,
  selectCurrentPageUrl,
  (navigation, currentPageUrl): IMenuItem | null => {
    if (!navigation) return null;
    let activeMenuItem = null;

    for (const navItem of navigation) {
      activeMenuItem = getMenuItem(navItem.subNav.items || [], currentPageUrl);
      if (activeMenuItem) {
        break;
      }
    }

    return activeMenuItem;
  },
);

const selectDateFilterType = createSelector(
  selectActiveNavMenuItem,
  (menuItem): EDateFilterType => menuItem?.data?.dateFilterType ?? EDateFilterType.period,
);

const selectShowScorecardFilter = createSelector(selectActiveNavMenuItem, (menuItem): boolean =>
  Boolean(menuItem?.data?.showScorecardFilter),
);

const selectNavigationConfig = (store: Store): Observable<INavItem[]> =>
  store.select(selectNavigation).pipe(filter<INavItem[] | null, INavItem[]>((nav): nav is INavItem[] => Boolean(nav)));

const selectCurrentBreadcrumb = createSelector(
  selectNavigation,
  selectCurrentPageUrl,
  selectUrl,
  (navigation, currentPageUrl, url): IMenuItem[] => {
    if (!navigation) return [];

    const currentModuleUrl = url.split('/').filter(Boolean)[0];
    const currentNavItem = navigation.find((navItem) => navItem.url === currentModuleUrl);
    return getBreadcrumbItems(currentNavItem?.subNav.items || [], currentPageUrl);
  },
);

export const APPLICATION_SELECTORS = {
  selectAppState,
  selectLoading,
  selectScreenSize,
  selectNavigationConfig,
  selectActiveNavMenuItem,
  selectIsDesktop,
  selectDateFilterType,
  selectCurrentBreadcrumb,
  selectFavouriteSubmenu,
  selectShowScorecardFilter,
};

const getBreadcrumbItems = (items: IMenuItem[], url: string): IMenuItem[] => {
  let isPageFound: boolean = false;

  const fillBreadcrumbItems = (items: IMenuItem[], breadcrumbItems: IMenuItem[]): IMenuItem[] => {
    for (const item of items) {
      if (isPageFound) {
        return breadcrumbItems;
      }
      if (item.items?.length) {
        handleGroupItem(item, breadcrumbItems);
      } else if (item.routerLink?.includes(url)) {
        isPageFound = true;
      }
    }
    return breadcrumbItems;
  };

  const handleGroupItem = (item: IMenuItem, breadcrumbItems: IMenuItem[]): void => {
    breadcrumbItems.push(item);
    fillBreadcrumbItems(item.items || [], breadcrumbItems);
    if (!isPageFound) breadcrumbItems.pop();
  };

  return fillBreadcrumbItems(items, []);
};

const getMenuItem = (items: IMenuItem[], url: string): IMenuItem | null => {
  const findItem = (item: IMenuItem): IMenuItem | null => {
    if (item.routerLink?.includes(url)) return item;
    if (item.items?.length) return getMenuItem(item.items, url);
    return null;
  };

  for (const item of items) {
    const result = findItem(item);
    if (result) return result;
  }

  return null;
};
